import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { SystemEntity } from '@erp/shared-types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Spinner } from '@/components/ui/spinner';
import { EntityNotesPanel } from '@/components/common/notes/EntityNotesPanel';
import { useEntityComments } from '@/api/rpc/hooks/core/useEntityComments';
import { InvoiceActivityList } from '@/components/accounts-payable/invoice-detail/invoice-change-log';
import { useInvoiceActivity } from '@/components/accounts-payable/invoice-detail/useInvoiceActivity';

function TabCount({ count }: { count: number | null }) {
  if (count === null || count === 0) return null;
  return <span className="tabular-nums"> ({count})</span>;
}

/**
 * Activity and Notes share one tab strip. Activity opens first: it is the
 * audit trail reviewers land on. Notes stays one tab away for the composer.
 */
export function InvoiceNotesActivityTabs({ invoiceId }: { invoiceId: string }) {
  const gt = useGT();
  const commentsQuery = useEntityComments({
    commentableType: SystemEntity.AP_INVOICE,
    commentableId: invoiceId,
  });
  const activity = useInvoiceActivity(invoiceId);
  const refreshLabel = gt('Refresh activity', {
    context:
      'icon button and tooltip on the AP invoice activity tab that reloads the audit trail',
  });

  const notesCount = commentsQuery.data
    ? commentsQuery.data.comments.length
    : null;
  const activityCount = activity.isLoading ? null : activity.entries.length;

  return (
    <Tabs defaultValue="activity" className="w-full">
      <TabsList className="w-full">
        <TabsTrigger value="activity">
          <T context="tab label: audit trail of changes and workflow runs on an AP invoice">
            Activity
          </T>
          {activity.isLoading ? (
            <Spinner
              size="sm"
              className="size-3"
              aria-label={gt('Loading activity', {
                context:
                  'spinner on the AP invoice activity tab while the audit trail loads',
              })}
            />
          ) : (
            <TabCount count={activityCount} />
          )}
        </TabsTrigger>
        <TabsTrigger value="notes">
          <T context="tab label: shared notes people leave on an AP invoice">
            Notes
          </T>
          <TabCount count={notesCount} />
        </TabsTrigger>
      </TabsList>
      <TabsContent value="activity" className="mt-0">
        <div className="max-h-96 overflow-y-auto rounded-b-md border border-t-0 px-2.5 py-1">
          <div className="flex justify-end">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    disabled={activity.isLoading}
                    onClick={() => void activity.refresh()}
                    aria-label={refreshLabel}
                  >
                    <ArrowClockwiseIcon className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{refreshLabel}</TooltipContent>
            </Tooltip>
          </div>
          <InvoiceActivityList {...activity} />
        </div>
      </TabsContent>
      <TabsContent value="notes" className="mt-0">
        <EntityNotesPanel
          key={invoiceId}
          entityType={SystemEntity.AP_INVOICE}
          entityId={invoiceId}
          heading={null}
          className="max-h-96 rounded-t-none border-t-0"
        />
      </TabsContent>
    </Tabs>
  );
}
